import { Color } from 'three';

/**
 * Het palet van New Amsterdam.
 *
 * De negen kernkleuren zijn gemeten uit de kleurenbalk van de styleboard; de rest is
 * afgeleid uit de panelen zelf. Dit bestand is de **enige** plek waar hex-waarden staan —
 * builders importeren namen, nooit getallen (zie AGENTS.md).
 */

export const PALETTE = {
  // Kernbalk van de styleboard
  slateGreen: 0x2b3a31,
  forestGreen: 0x1e3318,
  oxideMaroon: 0x5c2a22,
  brickRed: 0x7a3125,
  rustOrange: 0x8f4a18,
  sodiumAmber: 0xe0951c,
  neonCrimson: 0xb02333,
  deepTeal: 0x17384a,
  slateGrey: 0x43494b,

  // Nachtmassa's en constructie
  charcoal: 0x1b1c1e,
  nightBrick: 0x33241f,
  nightStone: 0x2a2c2e,
  asphalt: 0x232527,
  pavement: 0x3a3a38,
  weatheredWood: 0x4a3a2a,
  darkSteel: 0x2e3134,
  enamelWhite: 0xd8d2c2,
  harborNavy: 0x13283a,
  hullRed: 0x7a2a25,
  rubberBlack: 0x101316,

  // Water
  canalWater: 0x0e2b33,
  canalHighlight: 0x1d4d52,

  // Licht en emissie
  windowWarm: 0xffb64d,
  windowCold: 0x9fc4d8,
  lampGlow: 0xffcf8a,
  neonBlue: 0x2e7fb8,
  neonRed: 0xff3b52,
  headlight: 0xfff0c8,
  tailLight: 0xd21f2e,

  // Luca en personages — gemeten uit de character sheet
  skin: 0xd9a077,
  skinShadow: 0xa9764f,
  /** Bleke havenstad-huid: een leven onder natriumlicht. */
  skinPale: 0xe6bd97,
  /** Diepe warme huid — de haven is een immigrantenstad. */
  skinDeep: 0x7a5238,
  /** Warm donkerbruin haar, geen zwart: de sheet houdt bruin in de highlights. */
  hairBrown: 0x2f2119,
  hairBlack: 0x1a1512,
  /** Grijs haar en snor: een oudere Don, een uitgebluste agent, een burger van de buurt. */
  hairGrey: 0x8b847a,
  /** Stoppels op kaak en wang — een tint tussen huid en haar. */
  stubble: 0x8a5f42,
  tobaccoLeather: 0x4a3125,
  /** Schaduwzijde van het leer: panelen, kraagonderkant, manchetten. */
  leatherShadow: 0x33211a,
  bordeaux: 0x6e2430,
  charcoalOlive: 0x33301f,
  shoeBrown: 0x3b271b,
  gold: 0xc8891f,
  /** Schildpad-montuur: warm roodbruin, niet zwart. Luca's sterkste identiteitshaak. */
  tortoiseFrame: 0x7a4526,
  lensDark: 0x231d1a,
  /** Geborsteld staal: riemgesp, horlogekast. */
  steelBuckle: 0x9b9a94,
  cream: 0xe8ddc8,

  // Facties
  rivalNavy: 0x1f2733,
  rivalGrey: 0x4c4f52,
  policeBlue: 0x1b3350,
} as const;

export type PaletteName = keyof typeof PALETTE;

const cache = new Map<PaletteName, Color>();

/** Gedeelde, onveranderlijke Color voor een paletnaam. Niet muteren. */
export function color(name: PaletteName): Color {
  let value = cache.get(name);
  if (!value) {
    value = new Color(PALETTE[name]);
    cache.set(name, value);
  }
  return value;
}

/** Losse kopie, veilig om te muteren (bijvoorbeeld voor een tint-variatie). */
export function colorCopy(name: PaletteName): Color {
  return new Color(PALETTE[name]);
}

/**
 * Relatieve helderheid (0\u2013255), rechtstreeks uit de hex-waarde.
 *
 * Gebruikt om waardescheiding tussen kledingzones te toetsen \u2014 twee kleuren die er in de
 * naam anders uitzien kunnen alsnog dezelfde helderheid hebben en op miniatuurschaal als
 * \u00e9\u00e9n vlak samenvallen.
 */
export function luminance(name: PaletteName): number {
  const hex = PALETTE[name];
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}
